export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  featured?: boolean;
  content: string;
}

// Parse frontmatter manually
const parseFrontmatter = (content: string) => {
  const lines = content.split('\n');
  if (lines[0] !== '---') {
    return { data: {}, content };
  }

  let frontmatterEnd = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i] === '---') {
      frontmatterEnd = i;
      break;
    }
  }

  if (frontmatterEnd === -1) {
    return { data: {}, content };
  }

  const frontmatterLines = lines.slice(1, frontmatterEnd);
  const data: any = {};
  
  frontmatterLines.forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.slice(0, colonIndex).trim();
      let value: any = line.slice(colonIndex + 1).trim();
      
      // Remove quotes
      if ((value.startsWith('"') && value.endsWith('"')) || 
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      
      // Convert boolean strings
      if (value === 'true') value = true;
      if (value === 'false') value = false;
      
      data[key] = value;
    }
  });

  const markdownContent = lines.slice(frontmatterEnd + 1).join('\n');
  return { data, content: markdownContent };
};

// Import all markdown files from the blog directory
const blogPosts = import.meta.glob('/src/content/blog/*.md', { as: 'raw', eager: true });

export const getAllBlogPosts = (): BlogPost[] => {
  const posts: BlogPost[] = [];

  for (const path in blogPosts) {
    const content = blogPosts[path];
    const { data, content: markdownContent } = parseFrontmatter(content);
    
    // Extract slug from file path
    const slug = path.replace('/src/content/blog/', '').replace('.md', '');
    
    posts.push({
      slug,
      title: data.title,
      excerpt: data.excerpt,
      author: data.author,
      date: data.date,
      readTime: data.readTime,
      category: data.category,
      featured: data.featured || false,
      content: markdownContent
    });
  }

  // Sort by date (newest first)
  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

export const getBlogPost = (slug: string): BlogPost | null => {
  const posts = getAllBlogPosts();
  return posts.find(post => post.slug === slug) || null;
};

export const getFeaturedPosts = (): BlogPost[] => {
  return getAllBlogPosts().filter(post => post.featured);
};